import React, { useState, useRef } from 'react';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import MultiCompanySelect from '../selectors/company/MultiCompanySelect';
import { SFiltersPanel } from '@avalara/skylab-sdk/react';
import { IamUserRoleEnum } from '../../utils/enums';
import getClientConfig from '../../utils/getClientConfig';

const GenericFilterPanel = (props) => {
    const multiCompanySelectRef = useRef();
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [selectedUserName, setSelectedUserName] = useState('');

    const closeFilterPanel = () => {
        props.onDismiss();
    };

    const onCompanySelectionHandler = (e) => {
        setSelectedCompanies(prevState => [...prevState, e.value]);
    };

    const onCompanyDeselectionHandler = (e) => {
        setSelectedCompanies(prevState => prevState.filter(t => t !== e.value));
    };

    const onRoleSelectionHandler = (e) => {
        const isChecked = e.target.checked;
        const roleValue = e.target.value;

        if (isChecked) {
            setSelectedRoles(prevState => [...prevState, roleValue]);
        } else {
            setSelectedRoles(prevState => prevState.filter(t => t !== roleValue));
        }
    };

    const onPermissionSelectionHandler = (e) => {
        const isChecked = e.target.checked;
        const permissionValue = e.target.value;

        if (isChecked) {
            setSelectedPermissions(prevState => [...prevState, permissionValue]);
        } else {
            setSelectedPermissions(prevState => prevState.filter(t => t !== permissionValue));
        }
    };

    const onUserNameChange = (e) => {
        setSelectedUserName(e.target.value);
    };

    const setFilteringValues = () => {
        const config = { 
            companies: selectedCompanies, 
            roles: selectedRoles, 
            userName: selectedUserName 
        };
        
        if (props.showPermissionsFilter) {
            config.permissions = selectedPermissions;
        }
        
        props.setFilteringConfig(config);
    };

    const resetFilteringValues = () => {
        const config = { 
            companies: [], 
            roles: [], 
            userName: '' 
        };
        
        if (props.showPermissionsFilter) {
            config.permissions = [];
            setSelectedPermissions([]);
        }
        
        props.setFilteringConfig(config);
        setSelectedCompanies([]);
        setSelectedRoles([]);
        setSelectedUserName('');
        multiCompanySelectRef.current?.reset();
    };

    const enableReportingUserRole = getClientConfig()?.featureFlags?.EnableReportingUserRole;
    const enableReadOnlyRoleFilter = getClientConfig()?.featureFlags?.EnableReadOnlyRoleFilter;

    const renderRoleCheckbox = (id, testId, roleEnum, labelKey, enabled = false) => {
        if (!enabled) return null;
        
        return (
            <s-box class={selectedRoles.includes(roleEnum.value) ? "toggle-checkbox" : ""}>
                <fieldset>
                    <input
                        id={id}
                        type="checkbox"
                        data-testid={testId}
                        value={roleEnum.value}
                        checked={selectedRoles.includes(roleEnum.value)}
                        onChange={onRoleSelectionHandler}
                    />
                    <label className="checkbox bold pl33" htmlFor={id}>
                        <LanguageStringResolver string={labelKey} />
                    </label>
                </fieldset>
            </s-box>
        );
    };

    const renderPermissionCheckbox = (id, testId, permissionValue, labelKey) => (
        <s-box class={selectedPermissions.includes(permissionValue) ? "toggle-checkbox" : ""}>
            <fieldset>
                <input
                    id={id}
                    type="checkbox"
                    data-testid={testId}
                    value={permissionValue}
                    checked={selectedPermissions.includes(permissionValue)}
                    onChange={onPermissionSelectionHandler}
                />
                <label className="checkbox bold pl33" htmlFor={id}>
                    <LanguageStringResolver string={labelKey} />
                </label>
            </fieldset>
        </s-box>
    );

    return (
        <div>
            <SFiltersPanel
                open={props.open}
                className="mr30"
                onS-dismiss={closeFilterPanel}
                onS-reset={resetFilteringValues}
                onS-apply={setFilteringValues}
            >
                <div slot="filters-panel-content">
                    {/* Company Filter */}
                    <div className="full-width mb10">
                        <label><LanguageStringResolver string={`${props.filterPrefix}_Company`} /></label>
                        <MultiCompanySelect
                            ref={multiCompanySelectRef}
                            showSelectionCount={false}
                            selectionHandler={onCompanySelectionHandler}
                            deselectionHandler={onCompanyDeselectionHandler}
                            disabled={false}
                            allCompanyOptions={props.allCompanyOptions}
                        />
                    </div>

                    {/* Name Filter */}
                    <div className="full-width mb10">
                        <label><LanguageStringResolver string={`${props.filterPrefix}_Name`} /></label>
                        <input
                            id="name-filter-input"
                            data-testid="name-filter-input"
                            type="text"
                            value={selectedUserName}
                            onChange={onUserNameChange}
                        />
                    </div>

                    {/* Role Filter */}
                    <div className="full-width mb10">
                        <label><LanguageStringResolver string={`${props.filterPrefix}_Role`} /></label>
                        {renderRoleCheckbox("preparer-checkbox", "preparer-checkbox", IamUserRoleEnum.PREPARER, "Preparers", true)}
                        {renderRoleCheckbox("reporting-user-checkbox", "reporting-user-checkbox", IamUserRoleEnum.REPORTING_USER, "ReportingUsers", enableReportingUserRole)}
                        {renderRoleCheckbox("readonly-checkbox", "readonly-checkbox", IamUserRoleEnum.READ_ONLY, "ReadOnlyUsers", enableReadOnlyRoleFilter)}
                        {renderRoleCheckbox("admin-checkbox", "admin-checkbox", IamUserRoleEnum.ADMIN, "Administrators", true)}
                    </div>

                    {/* Permissions Filter (conditional) */}
                    {props.showPermissionsFilter && props.permissionOptions && (
                        <div className="full-width mb10">
                            <label><LanguageStringResolver string={`${props.filterPrefix}_Permissions`} /></label>
                            {props.permissionOptions.map(option => 
                                <React.Fragment key={option.value}>
                                    {renderPermissionCheckbox(
                                        `${option.value}-filter-checkbox`,
                                        `${option.value}-filter-checkbox`,
                                        option.value,
                                        option.labelKey
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    )}
                </div>
            </SFiltersPanel>
        </div>
    );
};


GenericFilterPanel.defaultProps = {
    showPermissionsFilter: false,
    permissionOptions: []
};

export default GenericFilterPanel;